import { ReactNode } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { AlertCircle, Home, RefreshCw, WifiOff, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { isNetworkError, isAuthError } from "@/utils/errorHandling";

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

const RouteErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleReload = () => {
    window.location.reload();
  };

  // Detect error type for contextual messaging
  const isNetwork = isNetworkError(error);
  const isAuth = isAuthError(error);

  const getErrorIcon = () => {
    if (isNetwork) return <WifiOff className="h-6 w-6" />;
    if (isAuth) return <ShieldAlert className="h-6 w-6" />;
    return <AlertCircle className="h-6 w-6" />;
  };

  const getErrorTitle = () => {
    if (isNetwork) return "Connection Error";
    if (isAuth) return "Authentication Error";
    return "Page Error";
  };

  const getErrorDescription = () => {
    if (isNetwork) {
      return "This page couldn't load due to a network issue. Check your connection and try again.";
    }
    if (isAuth) {
      return "Your session may have expired. Please sign in again.";
    }
    return "This page encountered an error and couldn't load properly.";
  };

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Network/Auth-specific alerts */}
      {(isNetwork || isAuth) && (
        <Alert variant="destructive" className="mb-4 max-w-2xl mx-auto">
          {getErrorIcon()}
          <AlertTitle>{getErrorTitle()}</AlertTitle>
          <AlertDescription>{getErrorDescription()}</AlertDescription>
        </Alert>
      )}

      <Card className="max-w-2xl mx-auto border-destructive/50">
        <CardHeader>
          <div className="flex items-center gap-2 text-destructive">
            {getErrorIcon()}
            <CardTitle>{getErrorTitle()}</CardTitle>
          </div>
          <CardDescription>
            {getErrorDescription()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Don't worry - your data is safe. Try one of the recovery options below.
          </p>

          <div className="flex flex-wrap gap-3">
            <Button onClick={resetErrorBoundary} variant="default">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            
            {isNetwork && (
              <Button onClick={handleReload} variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload Page
              </Button>
            )}
            
            <Button onClick={handleGoHome} variant="outline">
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Button>
          </div>

          {process.env.NODE_ENV === "development" && error && (
            <details className="mt-4 p-3 bg-muted rounded-md text-xs">
              <summary className="cursor-pointer font-medium">
                Error Details (Development)
              </summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words">
                {error.toString()}
                {error.stack && `\n\nStack trace:\n${error.stack}`}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Route-level error boundary for catching errors in individual pages
 * 
 * Features:
 * - Contextual error messages based on error type
 * - Recovery actions for network/auth errors
 * - Development mode stack traces
 * - Prevents full app crash from route-level errors
 */
const RouteErrorBoundary = ({ children }: RouteErrorBoundaryProps) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error("Route error caught:", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // Log to external service in production (optional)
    if (process.env.NODE_ENV === "production") {
      // Example: Send to error tracking service
      // logErrorToService(error, errorInfo);
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={RouteErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export default RouteErrorBoundary;
